import React, { useState, useEffect } from 'react';
import { CreditCard, AlertCircle, Mail, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import api from '../utils/api';


const ReactivateAccount = () => {
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [error, setError] = useState('');
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [reactivationSuccess, setReactivationSuccess] = useState(false);

  // Check if user just completed payment (returning from Stripe)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const success = urlParams.get('success');
    
    if (success === 'true' && sessionId) {
      // User returned from successful Stripe checkout
      verifyPaymentAndReactivate(sessionId);
    } else {
      // Normal page load - check subscription status
      checkSubscriptionStatus();
    }
  }, []);

  const checkSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        window.location.href = '/';
        return;
      }

      const response = await api.get(`/billing/subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSubscriptionInfo(response.data);
      
      // If subscription is already active, redirect to dashboard
      if (response.data.status === 'active') {
        window.location.href = '/';
      }
    } catch (err) {
      console.error('Failed to check subscription status:', err);
      // If no subscription found, they might need to sign up fresh
      if (err.response?.status === 404) {
        setError('No subscription found. Please contact support.');
      }
    } finally {
      setCheckingStatus(false);
    }
  };

  const verifyPaymentAndReactivate = async (sessionId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      
      // Verify the checkout session was completed
      const response = await api.get(`/billing/checkout/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.status === 'complete' || response.data.payment_status === 'paid') {
        setReactivationSuccess(true);
        // Wait a moment then redirect to dashboard
        setTimeout(() => {
          window.location.href = '/';
        }, 3000);
      } else {
        setError('Payment verification pending. Please wait a moment and refresh.');
        checkSubscriptionStatus();
      }
    } catch (err) {
      console.error('Failed to verify payment:', err);
      setError('Failed to verify payment. Please try again or contact support.');
      checkSubscriptionStatus();
    } finally {
      setLoading(false);
    }
  };

  const handleReactivate = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        window.location.href = '/';
        return;
      }

      // Call the reactivate endpoint to create a checkout session
      const response = await api.post(`/billing/reactivate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        if (response.data.status === 'active') {
          // Already active, redirect to dashboard
          window.location.href = '/';
        } else if (response.data.checkout_url) {
          // Redirect to Stripe checkout
          window.location.href = response.data.checkout_url;
        } else {
          setError('Unable to create checkout session. Please contact support.');
        }
      }
    } catch (err) {
      console.error('Reactivation failed:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to process request. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    window.location.href = '/';
  };

  // Show success state
  if (reactivationSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-green-500/30">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Account Reactivated!</h1>
              <p className="text-purple-200 mb-4">
                Your payment was successful. Redirecting you to the dashboard...
              </p>
              <div className="flex justify-center">
                <RefreshCw className="w-6 h-6 text-purple-400 animate-spin" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state while checking status
  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center px-4">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-purple-400 animate-spin mx-auto mb-4" />
          <p className="text-purple-200">Checking subscription status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-red-500/30">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Account Locked</h1>
            <p className="text-purple-200">
              Your subscription payment could not be processed and the grace period has expired.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200 flex items-start gap-2">
              <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* Subscription Info */}
          {subscriptionInfo && (
            <div className="bg-white/5 rounded-lg p-4 mb-6">
              <h3 className="text-white font-semibold mb-2">Subscription Details</h3>
              <div className="text-purple-200 text-sm space-y-1">
                <p><span className="text-purple-400">Status:</span> {subscriptionInfo.status || 'Locked'}</p>
                <p><span className="text-purple-400">Plan:</span> {subscriptionInfo.billing_cycle === 'annual' ? 'Annual' : 'Monthly'}</p>
                {subscriptionInfo.seats && (
                  <p><span className="text-purple-400">Seats:</span> {subscriptionInfo.seats}</p>
                )}
              </div>
            </div>
          )}

          {/* Info Box */}
          <div className="bg-white/5 rounded-lg p-4 mb-6">
            <h3 className="text-white font-semibold mb-2">What happened?</h3>
            <ul className="text-purple-200 text-sm space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-red-400">•</span>
                Your payment method was declined
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-400">•</span>
                We attempted to collect payment multiple times
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-400">•</span>
                The 10-day grace period has ended
              </li>
            </ul>
          </div>

          {/* What you can do */}
          <div className="bg-white/5 rounded-lg p-4 mb-6">
            <h3 className="text-white font-semibold mb-2">To restore access:</h3>
            <ol className="text-purple-200 text-sm space-y-2 list-decimal list-inside">
              <li>Click "Reactivate Account" below</li>
              <li>Complete payment on Stripe</li>
              <li>Your account will be reactivated immediately</li>
            </ol>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <button
              onClick={handleReactivate}
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5" />
                  Reactivate Account
                </>
              )}
            </button>

            <button
              onClick={handleLogout}
              className="w-full py-3 border border-purple-500/50 text-purple-200 font-medium rounded-xl hover:bg-purple-500/20 transition-colors"
            >
              Sign Out
            </button>
          </div>

          {/* Contact Support */}
          <div className="mt-6 pt-6 border-t border-purple-500/30 text-center">
            <p className="text-purple-300 text-sm mb-3">Need help? Contact support</p>
            <div className="flex justify-center gap-4">
              <a
                href="mailto:support@projectathena.us"
                className="flex items-center gap-2 text-purple-200 hover:text-white transition-colors text-sm"
              >
                <Mail className="w-4 h-4" />
                support@projectathena.us
              </a>
            </div>
          </div>

          {/* Data Safety Note */}
          <div className="mt-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
            <p className="text-green-200 text-xs text-center">
              ✓ Your data is safe. All your projects, clients, and files are preserved and will be available once you reactivate.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReactivateAccount;
